import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Users, Clock, MapPin, Home, Plane, AlertTriangle, RefreshCw } from "lucide-react";

const RANGES = [
  { days: 1, label: "Oggi" },
  { days: 7, label: "7 giorni" },
  { days: 30, label: "30 giorni" },
];

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const modeIcon = (m) => {
  if (m === "trasferta") return <Plane className="w-3 h-3 inline"/>;
  if (m === "smartworking") return <Home className="w-3 h-3 inline"/>;
  return <MapPin className="w-3 h-3 inline"/>;
};

export default function TeamAttendancesPage() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/supervisor/team-attendances?days=${days}`);
      setData(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  return (
    <div className="p-4 md:p-12" data-testid="team-attendances-page">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#757575] mb-2">Supervisione</div>
          <h1 className="font-display text-4xl">Timbrature Team</h1>
          <p className="text-[#555] mt-1 text-sm">Riepilogo aggregato del team · check-in, uscite, trasferte, smart working, anomalie</p>
        </div>
        <Button onClick={load} variant="outline" data-testid="team-refresh" className="rounded-full h-10">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`}/> Aggiorna
        </Button>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {RANGES.map((r) => (
          <button
            key={r.days}
            onClick={() => setDays(r.days)}
            data-testid={`team-range-${r.days}`}
            className={`rounded-full px-5 py-2.5 text-sm font-semibold flex items-center gap-2 transition-all ${days === r.days ? "bg-[#FF7A29] text-white shadow-lg shadow-orange-500/30" : "bg-white border border-[#EDE5D8] hover:border-[#FF7A29]"}`}
          >
            <Clock className="w-4 h-4"/> {r.label}
          </button>
        ))}
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="card-smart p-5 bg-[#0A0A0A] text-white border-[#0A0A0A]">
              <div className="text-[10px] uppercase tracking-widest opacity-70 mb-1">Dipendenti attivi</div>
              <div className="font-display text-3xl" data-testid="team-total-emps">{data.total_employees}</div>
            </div>
            <div className="card-smart p-5">
              <div className="text-[10px] uppercase tracking-widest text-[#757575] mb-1">Timbrature totali</div>
              <div className="font-display text-3xl" data-testid="team-total-atts">{data.total_attendances}</div>
            </div>
            <div className="card-smart p-5">
              <div className="text-[10px] uppercase tracking-widest text-[#757575] mb-1">Ingressi</div>
              <div className="font-display text-3xl">{data.team.reduce((s, t) => s + t.checkins, 0)}</div>
            </div>
            <div className="card-smart p-5">
              <div className="text-[10px] uppercase tracking-widest text-[#757575] mb-1">Anomalie</div>
              <div className="font-display text-3xl text-red-600">{data.team.reduce((s, t) => s + t.invalid_count, 0)}</div>
            </div>
          </div>

          <div className="card-smart overflow-hidden">
            <table className="w-full text-sm">
              <thead className="border-b border-[#EDE5D8]">
                <tr className="text-left">
                  <th className="p-4">Dipendente</th>
                  <th className="p-4">Ingressi</th>
                  <th className="p-4">Uscite</th>
                  <th className="p-4">Smart</th>
                  <th className="p-4">Trasferta</th>
                  <th className="p-4">Anomalie</th>
                  <th className="p-4">Ultima attività</th>
                </tr>
              </thead>
              <tbody>
                {data.team.length === 0 && (
                  <tr><td colSpan="7" className="p-12 text-center text-[#757575]">
                    <Users className="w-10 h-10 mx-auto mb-3 text-[#FF7A29]/50"/>
                    Nessuna timbratura registrata negli ultimi {days} giorni.
                  </td></tr>
                )}
                {data.team.map((row) => (
                  <tr key={row.user_id} data-testid={`team-row-${row.user_id}`} className="border-b border-[#EDE5D8] hover:bg-[#FFF0E3]/50">
                    <td className="p-4">
                      <div className="font-semibold">{row.full_name}</div>
                      <div className="text-xs text-[#757575]">{row.email}</div>
                    </td>
                    <td className="p-4 font-display text-lg">{row.checkins}</td>
                    <td className="p-4 font-display text-lg">{row.checkouts}</td>
                    <td className="p-4 text-sm">{row.smartworking_count > 0 ? <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 rounded-full px-2 py-0.5 text-xs font-semibold"><Home className="w-3 h-3"/> {row.smartworking_count}</span> : "—"}</td>
                    <td className="p-4 text-sm">{row.trasferta_count > 0 ? <span className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 rounded-full px-2 py-0.5 text-xs font-semibold"><Plane className="w-3 h-3"/> {row.trasferta_count}</span> : "—"}</td>
                    <td className="p-4 text-sm">{row.invalid_count > 0 ? <span className="inline-flex items-center gap-1 bg-red-100 text-red-700 rounded-full px-2 py-0.5 text-xs font-bold"><AlertTriangle className="w-3 h-3"/> {row.invalid_count}</span> : "—"}</td>
                    <td className="p-4 text-xs text-[#555]">
                      <div className="flex items-center gap-1">
                        {modeIcon(row.last_work_mode)}
                        <span className="font-semibold uppercase text-[10px]">{row.last_kind === "in" ? "Ingresso" : row.last_kind === "out" ? "Uscita" : row.last_kind}</span>
                      </div>
                      <div className="text-[#757575] mt-0.5">{fmtDateTime(row.last_activity)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
